import { Point } from '@printu/geom';
import getNextUID from '../markers/getNextUID';
/**
 * Generates arrow points
 */
export const generateSimpleArrowPoints = (p1, p2, arrowWidth) => {
    if (!p1 || !p2) {
        return null;
    }
    const aw = new Point(0.75, 12);
    const ab = new Point(0.8, 6);
    /*
                        * (10) y
                         \
        (0.0) -----------x- (1.0)
                        (0.95)
    */
    const aw2 = new Point(0, 0);
    const mouseDistance = p2.getDistance(p1);
    const mouseVector = p2.subtract(p1).normalize(1);
    let scale = 1.0;
    let tmp = 8 * arrowWidth;
    if (mouseDistance <= Math.floor(tmp)) {
        scale = mouseDistance / tmp;
    }
    // const delta = ab.x / aw.x;
    tmp = arrowWidth * 3.5 * scale;
    aw2.x = p2.x - tmp * mouseVector.x;
    aw2.y = p2.y - tmp * mouseVector.y;
    tmp = arrowWidth * 3 * scale;
    aw.y = arrowWidth * 2 * scale;
    ab.y = arrowWidth * scale;
    const safeLength = mouseDistance - arrowWidth * 0.8;
    const safeEnd = Point.from(safeLength * mouseVector.x, safeLength * mouseVector.y).add(p1);
    // perpendicular
    tmp = mouseVector.y;
    mouseVector.y = mouseVector.x;
    mouseVector.x = -tmp;
    return [
        // tail
        Point.from(p1.x, p1.y),
        safeEnd,
        // head left - tip - right
        Point.from(aw2.x + mouseVector.x * aw.y, aw2.y + mouseVector.y * aw.y),
        Point.from(p2.x, p2.y),
        Point.from(aw2.x - mouseVector.x * aw.y, aw2.y - mouseVector.y * aw.y),
    ];
};
export const generateSimpleArrowShape = (p1, p2, arrowWidth) => {
    const points = generateSimpleArrowPoints(p1, p2, arrowWidth);
    if (!points) {
        return null;
    }
    const [start, end, left, head, right] = points;
    return [
        {
            type: 'line',
            uid: getNextUID(),
            start,
            end,
        },
        {
            type: 'polyline',
            uid: getNextUID(),
            points: [left, head, right],
        },
    ];
};
/**
 * Generates fancy arrow points
 */
export const generateFancyArrowPoints = (p1, p2, arrowWidth) => {
    if (!p1 || !p2) {
        return [];
    }
    const aw = new Point(0.75, 12);
    const ab = new Point(0.8, 6);
    /*
                        * (10) y
                         \
        (0.0) ------------x--- (1.0)
                        (0.7)
    */
    const aw2 = new Point(0, 0);
    const ab2 = new Point(0, 0);
    const mouseDistance = p2.getDistance(p1);
    const mouseVector = p2.subtract(p1).normalize(1);
    let scale = 1.0;
    let tmp = 8 * arrowWidth;
    if (mouseDistance <= Math.floor(tmp)) {
        scale = mouseDistance / tmp;
    }
    // const delta = ab.x / aw.x;
    tmp = arrowWidth * 3.5 * scale;
    aw2.x = p2.x - tmp * mouseVector.x;
    aw2.y = p2.y - tmp * mouseVector.y;
    tmp = arrowWidth * 3 * scale;
    ab2.x = p2.x - tmp * mouseVector.x;
    ab2.y = p2.y - tmp * mouseVector.y;
    aw.y = arrowWidth * 2 * scale;
    ab.y = arrowWidth * scale;
    // perpendicular
    tmp = mouseVector.y;
    mouseVector.y = mouseVector.x;
    mouseVector.x = -tmp;
    return [
        new Point(p1.x, p1.y),
        new Point(ab2.x + mouseVector.x * ab.y, ab2.y + mouseVector.y * ab.y),
        new Point(aw2.x + mouseVector.x * aw.y, aw2.y + mouseVector.y * aw.y),
        new Point(p2.x, p2.y),
        new Point(aw2.x - mouseVector.x * aw.y, aw2.y - mouseVector.y * aw.y),
        new Point(ab2.x - mouseVector.x * ab.y, ab2.y - mouseVector.y * ab.y),
    ];
};
/**
 * Gets arrow triangulation for mouse hit testing
 */
export const getFancyArrowTriangulation = (p1, p2, arrowWidth) => {
    const [start, innerL, outerL, end, outerR, innerR] = generateFancyArrowPoints(p1, p2, arrowWidth);
    return [
        [start, innerR, innerL],
        [end, outerL, innerL],
        [end, innerR, outerR],
        [end, innerL, innerR],
    ];
};
export function pointInTriangle(points, p0, p1, p2) {
    const aCoef = (1 / 2) *
        (-p1.y * p2.x +
            p0.y * (-p1.x + p2.x) +
            p0.x * (p1.y - p2.y) +
            p1.x * p2.y);
    const sign = aCoef < 0 ? -1 : 1;
    const sParam = (p0.y * p2.x -
        p0.x * p2.y +
        (p2.y - p0.y) * points.x +
        (p0.x - p2.x) * points.y) *
        sign;
    const tParam = (p0.x * p1.y -
        p0.y * p1.x +
        (p0.y - p1.y) * points.x +
        (p1.x - p0.x) * points.y) *
        sign;
    return sParam > 0 && tParam > 0 && sParam + tParam < 2 * aCoef * sign;
}
